/**
 * Linguagem do contexto products no backoffice — espelha `products/domain`.
 */
import { z } from 'zod';
import type { components } from '@/core/http';

export type Product = components['schemas']['ProductOut'];
export type ProductBody = components['schemas']['ProductIn'];

export const BRANDS = [
  { id: 'central-nutrition', label: 'Central Nutrition' },
  { id: 'central-farma', label: 'Central Farma' },
  { id: 'central-injetaveis', label: 'Central Injetáveis' },
] as const;
export type BrandId = (typeof BRANDS)[number]['id'];

export const PACKAGINGS = [
  { id: 'box', label: 'Caixa / sachês' },
  { id: 'pot', label: 'Pote' },
  { id: 'bottle', label: 'Frasco' },
  { id: 'vial', label: 'Ampola' },
] as const;

export const STATUS_LABELS: Record<string, string> = {
  draft: 'Rascunho',
  published: 'Publicado',
  archived: 'Arquivado',
};

export const brandLabel = (id: string) => BRANDS.find((b) => b.id === id)?.label ?? id;

/**
 * Os mesmos números de `ProductIn` (products/presentation/schemas.py) e do
 * domínio: o que passa aqui passa lá.
 */
export const LIMITS = {
  name: { min: 3, max: 200 },
  summary: 300,
  blockTitle: 200,
  paragraph: 5000,
  paragraphs: 30,
  blocks: 30,
  ingredients: 60,
  ingredient: 200,
  amount: 100,
  position: 1_000_000,
} as const;

const max = (n: number) => `No máximo ${n.toLocaleString('pt-BR')} caracteres`;

/** O endereço do produto nasce do nome — precisa sobrar uma letra ou número sem acento. */
const makesSlug = (v: string) => /[a-z0-9]/i.test(v.normalize('NFKD').replace(/[^\p{ASCII}]/gu, ''));

/** Um bloco de texto no formulário: título e parágrafos separados por linha em branco. */
const blockSchema = z
  .object({
    title: z.string().max(LIMITS.blockTitle, max(LIMITS.blockTitle)),
    text: z
      .string()
      .refine((t) => toParagraphs(t).length <= LIMITS.paragraphs, {
        message: `No máximo ${LIMITS.paragraphs} parágrafos por bloco`,
      })
      .refine((t) => toParagraphs(t).every((p) => p.length <= LIMITS.paragraph), {
        message: `Cada parágrafo pode ter até ${LIMITS.paragraph.toLocaleString('pt-BR')} caracteres`,
      }),
  })
  // Bloco com título e sem texto seria descartado sem aviso.
  .refine((b) => !b.title.trim() || toParagraphs(b.text).length > 0, {
    message: 'Escreva o texto do bloco ou remova-o',
    path: ['text'],
  });
export type BlockForm = z.infer<typeof blockSchema>;

const ingredientSchema = z
  .object({
    ingredient: z.string().max(LIMITS.ingredient, max(LIMITS.ingredient)),
    amount: z.string().max(LIMITS.amount, max(LIMITS.amount)),
  })
  // Quantidade sem ingrediente seria descartada sem aviso.
  .refine((c) => !c.amount.trim() || !!c.ingredient.trim(), {
    message: 'Informe o ingrediente',
    path: ['ingredient'],
  });

const blocks = z.array(blockSchema).max(LIMITS.blocks, `No máximo ${LIMITS.blocks} blocos`);

export const productFormSchema = z.object({
  name: z
    .string()
    .trim()
    .min(LIMITS.name.min, `Mínimo de ${LIMITS.name.min} caracteres`)
    .max(LIMITS.name.max, max(LIMITS.name.max))
    .refine((v) => v.length < LIMITS.name.min || makesSlug(v), {
      message: 'Use ao menos uma letra ou número',
    }),
  /** Gerado pela API a partir do nome; o formulário só o devolve intacto. */
  slug: z.string(),
  summary: z.string().trim().max(LIMITS.summary, max(LIMITS.summary)),
  brand: z.enum(['central-nutrition', 'central-farma', 'central-injetaveis']),
  packaging: z.enum(['box', 'pot', 'bottle', 'vial']),
  bestSeller: z.boolean(),
  /** Como digitada: só dígitos. Vazio vale 0. */
  position: z
    .string()
    .refine((v) => v === '' || /^\d+$/.test(v), { message: 'Use só números inteiros' })
    .refine((v) => v === '' || Number(v) <= LIMITS.position, {
      message: `No máximo ${LIMITS.position.toLocaleString('pt-BR')}`,
    }),
  imageKey: z.string().nullable(),
  imageUrl: z.string().nullable(),
  description: blocks,
  usage: blocks,
  composition: z
    .array(ingredientSchema)
    .max(LIMITS.ingredients, `No máximo ${LIMITS.ingredients} ingredientes`),
});
export type ProductForm = z.infer<typeof productFormSchema>;

function toParagraphs(text: string): string[] {
  return text
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean);
}

const toBlocks = (blocks: BlockForm[]) =>
  blocks
    .map((b) => ({ title: collapseSpaces(b.title) || null, paragraphs: toParagraphs(b.text) }))
    .filter((b) => b.paragraphs.length > 0);

const fromBlocks = (blocks: { title?: string | null; paragraphs: string[] }[]): BlockForm[] =>
  blocks.map((b) => ({ title: b.title ?? '', text: b.paragraphs.join('\n\n') }));

export function emptyProductForm(): ProductForm {
  return {
    name: '',
    slug: '',
    summary: '',
    brand: 'central-nutrition',
    packaging: 'box',
    bestSeller: false,
    position: '0',
    imageKey: null,
    imageUrl: null,
    description: [{ title: '', text: '' }],
    usage: [{ title: '', text: '' }],
    composition: [],
  };
}

export function toProductForm(p: Product): ProductForm {
  return {
    name: p.name,
    slug: p.slug,
    summary: p.summary,
    brand: p.brand as BrandId,
    packaging: p.packaging as ProductForm['packaging'],
    bestSeller: p.best_seller,
    position: String(p.position),
    imageKey: p.image_key,
    imageUrl: p.image_url,
    description: fromBlocks(p.details.description),
    usage: fromBlocks(p.details.usage),
    composition: p.details.composition.map((c) => ({ ingredient: c.ingredient, amount: c.amount ?? '' })),
  };
}

export function toProductBody(f: ProductForm): ProductBody {
  return {
    name: collapseSpaces(f.name),
    slug: f.slug.trim() || null,
    summary: collapseSpaces(f.summary),
    brand: f.brand,
    packaging: f.packaging,
    best_seller: f.bestSeller,
    position: Number(f.position || 0),
    image_key: f.imageKey,
    details: {
      description: toBlocks(f.description),
      usage: toBlocks(f.usage),
      composition: f.composition
        .filter((c) => c.ingredient.trim())
        .map((c) => ({ ingredient: collapseSpaces(c.ingredient), amount: collapseSpaces(c.amount) || null })),
    },
  };
}

/** O que falta para ir ao portal — espelha `Product.publish_blockers`. */
export function publishBlockers(p: Pick<Product, 'name' | 'summary'>): string[] {
  const out: string[] = [];
  if (!p.name.trim()) out.push('Falta o nome.');
  if (!p.summary.trim()) out.push('Falta o resumo — ele aparece no card do portal.');
  return out;
}

// ── Formatação dos campos ──────────────────────────────────────────

/** Espaços repetidos e das pontas — o mesmo que a API faz. */
export const collapseSpaces = (v: string) => v.replace(/\s+/g, ' ').trim();

/** Ordem: só dígitos, sem zero à esquerda. */
export const digitsOnly = (v: string) => v.replace(/\D/g, '').replace(/^0+(?=\d)/, '');

/**
 * Texto de um bloco ao sair do campo: sem espaços no fim das linhas e no
 * máximo uma linha em branco entre parágrafos — é ela que separa um do outro.
 */
export const tidyText = (v: string) =>
  v
    .replace(/[ \t]+$/gm, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
